import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import {
  Lightning,
  Sun,
  Moon,
  ArrowRight,
  MagnifyingGlass,
  EnvelopeSimple,
  ShieldCheck,
  ChartBar,
  Check,
  CaretDown,
} from '@phosphor-icons/react';
import api from '../lib/api';

const PRO_EMAIL_START_URL = 'https://pro-email.app/register';
const FEATURE_ICONS = [MagnifyingGlass, Lightning, EnvelopeSimple, ShieldCheck, ChartBar, Check];

function LandingNav() {
  const { t } = useTranslation('landing');
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { key: 'features', href: '#features' },
    { key: 'howItWorks', href: '#how-it-works' },
    { key: 'pricing', href: '#pricing' },
    { key: 'faq', href: '#faq' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/90 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <Lightning size={22} weight="fill" className="text-foreground" />
          <span className="font-heading text-lg font-bold tracking-tight text-foreground hidden sm:inline">Mayr Lead Creator</span>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {links.map((link) => (
            <a key={link.href} href={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t(`nav.${link.key}`)}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSelector className="w-24 sm:w-28" />
          <button onClick={toggleTheme} aria-label={t('nav.toggleTheme')} className="text-muted-foreground hover:text-foreground p-1.5">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Link to="/signin" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block">
            {t('nav.signIn')}
          </Link>
          <a href={PRO_EMAIL_START_URL} className="hidden sm:inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
            {t('nav.start')}
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  const { t } = useTranslation('landing');
  return (
    <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-muted/50 text-xs font-mono text-muted-foreground mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {t('hero.badge')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-foreground leading-[1.1] mb-5">
          {t('hero.line1')}<br />
          <span className="text-muted-foreground">{t('hero.line2')}</span><br />
          {t('hero.line3')}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">{t('hero.body')}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href={PRO_EMAIL_START_URL} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity text-base">
            {t('hero.primary')} <ArrowRight size={18} />
          </a>
          <a href="#how-it-works" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-border text-foreground font-medium hover:bg-accent transition-colors text-base">
            {t('hero.secondary')}
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-4">{t('hero.note')}</p>
      </div>
    </section>
  );
}

function Problem() {
  const { t } = useTranslation('landing');
  return (
    <section className="py-20 px-6 border-t border-border">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-4">{t('problem.manual.title')}</h2>
          <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
            <p>{t('problem.manual.first')}</p>
            <p>{t('problem.manual.second')}</p>
          </div>
        </div>
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-4">{t('problem.automate.title')}</h2>
          <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
            <p>{t('problem.automate.first')}</p>
            <p>{t('problem.automate.second')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useTranslation('landing');
  const items = t('features.items', { returnObjects: true });
  return (
    <section id="features" className="py-20 px-6 border-t border-border bg-muted/20">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">{t('features.title')}</h2>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto">{t('features.subtitle')}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, index) => {
            const Icon = FEATURE_ICONS[index];
            return (
              <div key={item.title} className="p-6 rounded-lg border border-border bg-background hover:border-foreground/20 transition-colors">
                <Icon size={24} className="text-foreground mb-3" weight="bold" />
                <h3 className="font-heading font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation('landing');
  const steps = t('how.steps', { returnObjects: true });
  return (
    <section id="how-it-works" className="py-20 px-6 border-t border-border">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">{t('how.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('how.subtitle')}</p>
        </div>
        <div className="space-y-6">
          {steps.map((step, index) => (
            <div key={step.title} className="flex gap-5 items-start">
              <div className="w-12 h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-sm shrink-0">{String(index + 1).padStart(2, '0')}</div>
              <div className="pt-1">
                <h3 className="font-heading font-bold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhoItsFor() {
  const { t } = useTranslation('landing');
  const audiences = t('audience.items', { returnObjects: true });
  return (
    <section className="py-20 px-6 border-t border-border bg-muted/20">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">{t('audience.title')}</h2>
        <p className="text-muted-foreground text-sm mb-8">{t('audience.subtitle')}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {audiences.map((audience) => <span key={audience} className="px-4 py-2 rounded-full border border-border bg-background text-sm text-foreground">{audience}</span>)}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { t, i18n } = useTranslation('landing');
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const locale = i18n.resolvedLanguage === 'de' ? 'de-DE' : i18n.resolvedLanguage === 'es' ? 'es-ES' : 'en-GB';

  useEffect(() => {
    api.get('/saas/plans').then((response) => setPlans(response.data)).catch(() => {});
  }, []);

  const formatNumber = (value) => new Intl.NumberFormat(locale).format(value);

  return (
    <section id="pricing" className="py-20 px-6 border-t border-border">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">{t('pricing.title')}</h2>
          <p className="text-muted-foreground text-sm mb-5">{t('pricing.subtitle')}</p>
          <div className="inline-flex items-center gap-2 p-1 rounded-lg border border-border bg-muted/50">
            <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${!yearly ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>{t('pricing.monthly')}</button>
            <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${yearly ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>
              {t('pricing.yearly')} <span className="text-xs text-emerald-600 ml-1">{t('pricing.save')}</span>
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const highlighted = plan.name === 'growth';
            const price = yearly ? Math.round(plan.price_yearly / 12) : plan.price_monthly;
            return (
              <div key={plan.id} className={`rounded-lg border p-6 flex flex-col ${highlighted ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-background'}`}>
                {highlighted && <span className="text-xs font-mono uppercase tracking-wider text-primary mb-3">{t('pricing.popular')}</span>}
                <h3 className="font-heading text-xl font-bold text-foreground">{plan.display_name}</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-4">{t(`pricing.planDescriptions.${plan.name}`, { defaultValue: plan.description })}</p>
                <div className="mb-5">
                  <span className="text-3xl font-heading font-bold text-foreground">€{price}</span>
                  <span className="text-muted-foreground text-sm">{t('pricing.perMonth')}</span>
                  {yearly && <span className="text-xs text-muted-foreground ml-2">{t('pricing.billedYearly')}</span>}
                </div>
                <a href={PRO_EMAIL_START_URL} className={`w-full text-center py-2.5 rounded-md font-medium text-sm transition-opacity hover:opacity-90 mb-5 block ${highlighted ? 'bg-primary text-primary-foreground' : 'bg-foreground text-background'}`}>
                  {t('pricing.start')}
                </a>
                <ul className="space-y-2 flex-1 text-sm">
                  <PlanFeature>{t('pricing.features.jobs', { count: formatNumber(plan.max_discovery_jobs) })}</PlanFeature>
                  <PlanFeature>{t('pricing.features.companies', { count: formatNumber(plan.max_companies) })}</PlanFeature>
                  <PlanFeature>{t('pricing.features.contacts', { count: formatNumber(plan.max_contacts) })}</PlanFeature>
                  <PlanFeature>{t('pricing.features.seats', { count: formatNumber(plan.max_seats) })}</PlanFeature>
                  <PlanFeature>{t('pricing.features.exports', { count: formatNumber(plan.max_exports) })}</PlanFeature>
                  {plan.has_pro_email_integration && <PlanFeature>{t('pricing.features.proEmail')}</PlanFeature>}
                  {plan.has_bulk_verification && <PlanFeature>{t('pricing.features.bulkVerification')}</PlanFeature>}
                  {plan.has_custom_scoring && <PlanFeature>{t('pricing.features.scoring')}</PlanFeature>}
                  {plan.has_api_access && <PlanFeature>{t('pricing.features.api')}</PlanFeature>}
                  {plan.has_priority_support && <PlanFeature>{t('pricing.features.support')}</PlanFeature>}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground text-center mt-6">{t('pricing.note')}</p>
      </div>
    </section>
  );
}

function PlanFeature({ children }) {
  return <li className="flex items-start gap-2 text-foreground"><Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {children}</li>;
}

function FAQ() {
  const { t } = useTranslation('landing');
  const [open, setOpen] = useState(null);
  const items = t('faq.items', { returnObjects: true });
  return (
    <section id="faq" className="py-20 px-6 border-t border-border bg-muted/20">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground text-center mb-10">{t('faq.title')}</h2>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={item.question} className="border border-border rounded-lg bg-background overflow-hidden">
              <button onClick={() => setOpen(open === index ? null : index)} className="w-full text-left px-5 py-4 flex items-center justify-between text-sm font-medium text-foreground hover:bg-accent/50 transition-colors">
                {item.question}
                <CaretDown size={16} className={`text-muted-foreground transition-transform ${open === index ? 'rotate-180' : ''}`} />
              </button>
              {open === index && <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">{item.answer}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useTranslation('landing');
  return (
    <section className="py-20 px-6 border-t border-border">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-4">{t('final.title')}</h2>
        <p className="text-muted-foreground text-sm mb-6 max-w-lg mx-auto">{t('final.body')}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href={PRO_EMAIL_START_URL} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity">
            {t('final.primary')} <ArrowRight size={18} />
          </a>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-border text-foreground font-medium hover:bg-accent transition-colors">{t('final.demo')}</Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation('landing');
  return (
    <footer className="border-t border-border py-10 px-6">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Lightning size={18} weight="fill" className="text-foreground" />
          <span className="font-heading text-sm font-bold text-foreground">Mayr Lead Creator</span>
          <span className="text-xs text-muted-foreground ml-2">{t('footer.developed')}</span>
        </div>
        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <Link to="/terms" className="hover:text-foreground transition-colors">{t('footer.terms')}</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer.privacy')}</Link>
          <Link to="/contact" className="hover:text-foreground transition-colors">{t('footer.contact')}</Link>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingNav />
      <Hero />
      <Problem />
      <Features />
      <HowItWorks />
      <WhoItsFor />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
